import { asyncPipe, pipe, tap } from '@helo/utils'
import { StateCreator } from 'zustand'

import { createRouter } from '../../../router/router'
import { RouteConfig } from '../../../router/routes'
import { WidgetConfig } from '../../../widgets/widget-types'
import { AuthSlice } from '../auth/authSlice'
import { CmsSlice } from '../cms'
import { PersistenceSlice } from '../persistence/persistenceSlice'
import { QuerySlice } from '../queryStore/querySlice'
import { ViewSlice } from '../view/viewSlice'
import { RouterSliceActions } from './routerSlice.actions'

export type RouterState = {
  router: ReturnType<typeof createRouter> | null
  routes: RouteConfig[]
  routeConfig: RouteConfig | null
  componentStatus: 'initial' | 'initializing' | 'ready'
  uniqueViewKey: string
  pathname: string
  urlParams?: Record<string, string>
  searchParams: Record<string, string>
  screen: string | null
  layout: WidgetConfig | null
  /**
   * Latest committed page-setup run; used to cancel stale async pipelines when
   * multiple `runPageSetup` flows overlap.
   */
  pageSetupRunId: string | null
  /**
   * Tracks whether route watch has been initialized.
   * Prevents duplicate initialization from React StrictMode double-renders.
   */
  routeWatchInitialized: boolean
  /**
   * listens to route changes to tear down and set up page states.
   */
  startRouteWatch: () => () => void
  initialize: () => void
  getRouteById: (routeId: string) => RouteConfig | undefined
}

export type RouterSlice = {
  router: RouterState
}

export const routerSlice: StateCreator<
  RouterSlice &
    AuthSlice &
    PersistenceSlice &
    QuerySlice &
    ViewSlice &
    CmsSlice,
  [],
  [],
  RouterSlice
> = (set, get, storeApi) => ({
  router: {
    router: null,
    routes: [],
    pathname: '',
    searchParams: {},
    componentStatus: 'initial',
    uniqueViewKey: '',
    routeConfig: null,
    urlParams: {},
    layout: null,
    screen: null,
    pageSetupRunId: null,
    routeWatchInitialized: false,
    initialize: () => {
      pipe(get(), RouterSliceActions.initializeRouter(), set)
    },
    getRouteById: routeId => {
      return get().router.routes.find(route => route.routeId === routeId)
    },
    startRouteWatch: () => {
      // Guard against React StrictMode double-renders.
      // Only run initial page setup once, even if this function is called multiple times.
      // But we always need to set up the subscription since StrictMode cleanup may have removed it.
      const isFirstRun = !get().router.routeWatchInitialized

      if (isFirstRun) {
        // Mark as initialized before running to prevent race conditions
        set(store => ({
          ...store,
          router: { ...store.router, routeWatchInitialized: true },
        }))

        // Run initial page setup only on first call
        asyncPipe(
          get(),
          RouterSliceActions.runPageSetup(storeApi),
          store => set(store),
          () => get(),
          asyncPipe.catch(error => {
            console.error(error)
            return get()
          }),
        )
      }

      // Always set up the subscription (it may have been cleaned up by StrictMode)
      const unsubscribe = get().router!.router!.subscribe(() => {
        asyncPipe(
          get(),
          RouterSliceActions.runPageSetup(storeApi),
          store => set(store),
          () => get(),
          asyncPipe.catch(error => {
            console.error(error)
            return get()
          }),
        )
      })
      return unsubscribe
    },
  },
})
